import type { PrimaryEntityId } from "building-types/doc/primaryEntityId";
import type { FormulaDisabledDocEntityBase } from "building-types/entities/docEntityBase";
import type { ExteriorMaterial } from "building-types/materials/exteriorMaterialTypes";
import type { ExteriorMaterialOverride } from "building-types/materials/materialTypes";
import type { SimpleProp } from "building-types/props/propTypes";
import type GeomTagGroup from "geom/geomTagGroup";
import type { PolyLine3 } from "geom/polyLine3";
import type { ContextHomeProp } from "./ContextHomeProp";
import type { SiteGeomTagGroup } from "./siteGeomTagGroup";
import type { SiteShape } from "./siteShape";

export enum BuiltInSiteType {
  DYNAMIC = "DYNAMIC",
  STATIC_CONTOUR = "STATIC_CONTOUR",
  WHITE_BOX = "WHITE_BOX",
}

export enum SiteResolutionLevel {
  COARSE = "COARSE",
  MEDIUM = "MEDIUM",
  FINE = "FINE",
}
export interface Site extends FormulaDisabledDocEntityBase<"SITE"> {
  readonly id: PrimaryEntityId<"SITE">;
  readonly type?: BuiltInSiteType;

  // Contour Site Controls
  readonly fittingMode?: SiteFittingMode;
  readonly surfaceMaterial?: ExteriorMaterial;
  readonly siteResolution?: SiteResolutionLevel;

  // Dynamic / White Box Site Controls
  readonly sideSetback?: number;
  readonly backSetback?: number;
  readonly frontSetback?: number;

  readonly roadWidth?: number;
  readonly backRoad?: boolean;

  readonly homeBackVerticalOffset?: number;
  readonly homeFrontVerticalOffset?: number;

  readonly siteBaseElevation?: number;

  readonly sidewalkWidth?: number;
  readonly sidewalkRampLength?: number;
  readonly grassCurbWidth?: number;
  readonly curbHeight?: number;

  readonly frontWalk?: boolean;
  readonly frontWalkConnectedToDriveway?: boolean;
  readonly frontWalkConnectedSetback?: number;
  readonly frontWalkWidth?: number;

  readonly driveway?: boolean;
  readonly drivewayMaterial?: ExteriorMaterial;

  readonly sidewalk?: boolean;
  readonly curb?: boolean;
  readonly fence?: boolean;
  readonly contextHomes?: ContextHomeProp;
  readonly fenceHeight?: number;
  readonly fenceSetback?: number;

  readonly roadVerticalOffset?: number;
}

export enum SiteFittingMode {
  TIGHT = "TIGHT",
  MEDIUM = "MEDIUM",
  LOOSE = "LOOSE",
}

export interface GeneratedSite extends Site {
  readonly propPlacements: SimpleProp[];

  readonly siteDatum: PolyLine3[];
  readonly setbackDatum: PolyLine3[];
  readonly sidewalkDatum: PolyLine3[];

  readonly drivewayGeom: SiteGeomTagGroup[];
  readonly sidewalkGeom: SiteGeomTagGroup[];
  readonly siteWhiteBoxGeom: GeomTagGroup<ExteriorMaterialOverride>;
  readonly fenceGeom: SiteGeomTagGroup[];
  readonly grassGeom: SiteGeomTagGroup[];
  readonly roadGeom: SiteGeomTagGroup[];
  readonly curbGeom: SiteGeomTagGroup[];
  readonly topographyGeom: GeomTagGroup<ExteriorMaterialOverride>;
  readonly sitePlaneGeom: GeomTagGroup<ExteriorMaterialOverride>;

  readonly siteShapes: SiteShape[];
}
